import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Sparkles, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Story Understanding panel (Stage-1 cache surface) — Phase 2c.
 *
 * Renders the `scripts.story_understanding` payload returned in the
 * `/api/projects/{id}/beat-board` response. Supports:
 *   • Empty state — "Generate Understanding" button
 *   • Stale state — yellow banner + regenerate
 *   • Collapsed/expanded body
 */
export default function StoryUnderstandingPanel({
  scriptId,
  storyUnderstanding,
  scriptUpdatedAt,
  onRegenerated,
  token,
}) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const isEmpty = !storyUnderstanding;
  const isStale = Boolean(
    storyUnderstanding &&
      (storyUnderstanding.invalidated_at ||
        (!scriptUpdatedAt ||
          (storyUnderstanding.generated_at &&
          new Date(storyUnderstanding.generated_at) < new Date(scriptUpdatedAt)))),
  );

  const generate = async (force = false) => {
    if (!scriptId) {
      toast.error('No script available — upload one first.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/scripts/${scriptId}/story-understanding/generate`,
        { force_regenerate: force },
        { headers },
      );
      if (data.status === 'budget_blocked') {
        toast.error('Daily AI generation limit reached — try again after UTC midnight.');
      } else if (data.status === 'validation_failed') {
        toast.error('AI returned invalid output — please retry.');
      } else if (data.status === 'generated' || data.status === 'cached') {
        toast.success(
          data.status === 'cached' ? 'Story understanding loaded from cache.' : 'Story understanding generated.',
        );
        onRegenerated && onRegenerated();
      } else if (data.status === 'in_flight') {
        toast.info('Another generation is already in flight.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed.');
    } finally {
      setBusy(false);
    }
  };

  // ── EMPTY STATE ──────────────────────────────────────────────────────────
  if (isEmpty) {
    return (
      <div
        data-testid="story-understanding-panel"
        className="mx-6 mt-4 rounded-xl border p-5 flex items-center justify-between gap-4"
        style={{ background: 'rgba(96,165,250,0.05)', borderColor: 'rgba(96,165,250,0.2)' }}
      >
        <div>
          <p className="text-sm font-bold mb-1" style={{ color: '#60a5fa' }}>
            Story Understanding (Stage 1)
          </p>
          <p className="text-xs" style={{ color: 'var(--cb-text-sec)' }}>
            Generate a holistic read of premise, characters & themes — required before beat extraction.
          </p>
        </div>
        <Button
          onClick={() => generate(false)}
          disabled={busy || !scriptId}
          data-testid="generate-understanding-btn"
          className="font-semibold"
          style={{ background: '#60a5fa', color: '#000' }}
        >
          {busy ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Generating…</> :
                  <><Sparkles className="h-4 w-4 mr-2" /> Generate Understanding</>}
        </Button>
      </div>
    );
  }

  const su = storyUnderstanding;
  const generatedAt = su.generated_at ? new Date(su.generated_at) : null;
  const relTime = generatedAt
    ? Math.round((Date.now() - generatedAt.getTime()) / 60000)
    : null;

  return (
    <div
      data-testid="story-understanding-panel"
      className="mx-6 mt-4 rounded-xl border overflow-hidden"
      style={{ background: 'var(--cb-surface)', borderColor: 'var(--cb-border)' }}
    >
      {/* Header */}
      <button
        type="button"
        className="w-full px-5 py-3 flex items-center justify-between gap-3 hover:bg-white/[0.02] transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Sparkles className="h-4 w-4 shrink-0" style={{ color: '#60a5fa' }} />
          <span className="text-sm font-bold" style={{ color: 'var(--cb-text)' }}>
            Story Understanding
          </span>
          {relTime !== null && (
            <span className="text-[11px] font-mono" style={{ color: 'var(--cb-text-dim)' }}>
              {relTime < 1 ? 'just now' : `${relTime}m ago`}
            </span>
          )}
          <span className="text-[10px] px-2 py-0.5 rounded font-mono"
                style={{ background: 'var(--cb-surface-h)', color: 'var(--cb-text-dim)' }}>
            FiPr AI
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Always-visible Regenerate ⟳ — Fix 2 (M3.15) so users can refresh
              cached SU without depending on the stale-banner staleness check.
              span+onClick (not button) to avoid nested-button HTML invalidity */}
          <span
            role="button"
            tabIndex={0}
            data-testid="regenerate-understanding-btn"
            title="Regenerate Story Understanding"
            aria-disabled={busy || !scriptId}
            onClick={(e) => { e.stopPropagation(); if (!busy && scriptId) generate(true); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !busy && scriptId) { e.stopPropagation(); generate(true); } }}
            className={`h-6 w-6 rounded-full flex items-center justify-center transition-transform ${
              (busy || !scriptId) ? 'opacity-40 cursor-not-allowed' : 'hover:scale-110 cursor-pointer'
            }`}
            style={{ background: 'var(--cb-surface-h)', color: '#60a5fa' }}
          >
            <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} />
          </span>
          {isStale && (
            <span className="text-[10px] px-2 py-1 rounded uppercase font-bold tracking-wider flex items-center gap-1"
                  style={{ background: 'rgba(245,166,35,0.15)', color: '#F5A623' }}>
              <AlertTriangle className="h-3 w-3" /> Outdated
            </span>
          )}
          {expanded ? <ChevronUp className="h-4 w-4" style={{ color: 'var(--cb-text-dim)' }} />
                    : <ChevronDown className="h-4 w-4" style={{ color: 'var(--cb-text-dim)' }} />}
        </div>
      </button>

      {/* Stale banner */}
      {isStale && (
        <div className="px-5 py-2 flex items-center justify-between gap-3 border-t"
             style={{ background: 'rgba(245,166,35,0.05)', borderColor: 'rgba(245,166,35,0.2)' }}>
          <p className="text-xs" style={{ color: 'var(--cb-text)' }}>
            Script has changed since this understanding was generated.
          </p>
          <Button
            size="sm"
            onClick={() => generate(true)}
            disabled={busy}
            data-testid="regenerate-understanding-btn"
            style={{ background: '#F5A623', color: '#000' }}
            className="font-semibold h-7 text-xs"
          >
            {busy ? <><RefreshCw className="h-3 w-3 mr-1 animate-spin" /> Regenerating…</>
                  : <><RefreshCw className="h-3 w-3 mr-1" /> Regenerate</>}
          </Button>
        </div>
      )}

      {/* Body */}
      {expanded && (
        <div className="px-5 py-4 border-t space-y-4" style={{ borderColor: 'var(--cb-border)' }}>
          {su.central_premise && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold mb-1"
                 style={{ color: 'var(--cb-text-dim)' }}>Central Premise</p>
              <p className="text-sm italic leading-relaxed" style={{ color: 'var(--cb-text)' }}>
                &ldquo;{su.central_premise}&rdquo;
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {su.genre && (
              <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
                    style={{ background: '#60a5fa20', color: '#60a5fa' }}>
                {su.genre}
              </span>
            )}
            {su.narrative_structure && (
              <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
                    style={{ background: '#fb718520', color: '#fb7185' }}>
                {su.narrative_structure}
              </span>
            )}
          </div>

          {Array.isArray(su.tone_signals) && su.tone_signals.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold mb-2"
                 style={{ color: 'var(--cb-text-dim)' }}>Tone Signals</p>
              <div className="flex flex-wrap gap-1.5">
                {su.tone_signals.map((t, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full"
                        style={{ background: 'var(--cb-surface-h)', color: 'var(--cb-text-sec)' }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(su.main_characters) && su.main_characters.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold mb-2"
                 style={{ color: 'var(--cb-text-dim)' }}>Main Characters</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {su.main_characters.map((c, i) => (
                  <div key={i} className="rounded-lg p-3 border"
                       style={{ background: 'var(--cb-surface-h)', borderColor: 'var(--cb-border)' }}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-sm" style={{ color: 'var(--cb-text)' }}>{c.name}</span>
                      {c.role && (
                        <span className="text-[10px] uppercase tracking-wider font-bold"
                              style={{ color: '#F5A623' }}>{c.role}</span>
                      )}
                    </div>
                    {c.arc_summary && (
                      <p className="text-[11px]" style={{ color: 'var(--cb-text-sec)' }}>
                        {c.initial_state && <span style={{ color: 'var(--cb-text-dim)' }}>{c.initial_state} → </span>}
                        {c.arc_summary}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(su.theme_tags) && su.theme_tags.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold mb-2"
                 style={{ color: 'var(--cb-text-dim)' }}>Theme Tags (canon)</p>
              <div className="flex flex-wrap gap-1.5">
                {su.theme_tags.map((t, i) => (
                  <span key={i} className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
                        style={{ background: '#4ade8020', color: '#4ade80' }}>
                    #{t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
